import { useState, useEffect, useRef, useCallback } from 'react';

export const useKioskInactivity = ({
  idleTimeout = 60,
  warningDuration = 20,
  enabled = true,
  onTimeout,
}) => {
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(warningDuration);

  const enabledRef = useRef(enabled);
  const idleTimeoutRef = useRef(idleTimeout);
  const warningDurationRef = useRef(warningDuration);
  const onTimeoutRef = useRef(onTimeout);

  const lastActivityTimeRef = useRef(Date.now());
  const isWarningOpenRef = useRef(false);
  const warningStartTimeRef = useRef(0);
  const isTimedOutRef = useRef(false);
  const fallbackTimeoutRef = useRef(null);

  useEffect(() => {
    enabledRef.current = enabled;
    idleTimeoutRef.current = idleTimeout;
    warningDurationRef.current = warningDuration;
    onTimeoutRef.current = onTimeout;

    if (enabled) {
      if (!isWarningOpenRef.current && !isTimedOutRef.current) {
        lastActivityTimeRef.current = Date.now();
      }
    } else {
      isTimedOutRef.current = false;
      isWarningOpenRef.current = false;
      setIsWarningOpen(false);
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
    }
  }, [enabled, idleTimeout, warningDuration, onTimeout]);

  const resetTimer = useCallback(() => {
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
    isTimedOutRef.current = false;
    lastActivityTimeRef.current = Date.now();
    isWarningOpenRef.current = false;
    setIsWarningOpen(false);
    setRemainingSeconds(warningDurationRef.current);
  }, []);

  const triggerTimeout = useCallback((args) => {
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
    isTimedOutRef.current = true;
    isWarningOpenRef.current = false;
    setIsWarningOpen(false);
    if (onTimeoutRef.current) {
      onTimeoutRef.current(args);
    }
  }, []);

  useEffect(() => {
    const handleActivity = () => {
      if (!isWarningOpenRef.current && !isTimedOutRef.current) {
        lastActivityTimeRef.current = Date.now();
      }
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    const intervalId = setInterval(() => {
      if (!enabledRef.current || isTimedOutRef.current) return;

      const now = Date.now();

      if (!isWarningOpenRef.current) {
        const idleElapsedSeconds = (now - lastActivityTimeRef.current) / 1000;
        if (idleElapsedSeconds >= idleTimeoutRef.current) {
          isWarningOpenRef.current = true;
          warningStartTimeRef.current = now;
          setRemainingSeconds(warningDurationRef.current);
          setIsWarningOpen(true);
        }
      } else {
        const warningElapsedSeconds = (now - warningStartTimeRef.current) / 1000;
        const remaining = Math.max(
          0,
          Math.ceil(warningDurationRef.current - warningElapsedSeconds)
        );

        setRemainingSeconds(remaining);

        if (remaining <= 0) {
          isTimedOutRef.current = true;
          // Do not abruptly close the modal here to avoid closing & reopening.
          // Fallback timer: if modal cancel doesn't complete within 10s, force cleanup.
          if (!fallbackTimeoutRef.current) {
            fallbackTimeoutRef.current = setTimeout(() => {
              isWarningOpenRef.current = false;
              setIsWarningOpen(false);
              if (onTimeoutRef.current) {
                onTimeoutRef.current();
              }
            }, 10000);
          }
        }
      }
    }, 500);

    return () => {
      clearInterval(intervalId);
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, []);

  return {
    isWarningOpen,
    remainingSeconds,
    resetTimer,
    triggerTimeout,
  };
};

export default useKioskInactivity;
